<div class="fixed inset-0 z-50 flex justify-end" aria-modal="true">
  <!-- Overlay -->
  <div class="absolute inset-0 bg-[#363738] bg-opacity-25 backdrop-blur-sm transition-opacity dark:bg-[#3333331f]"
    (click)="onCloseModal()"></div>

  <!-- Slideout -->
  <div
    class="relative w-full max-w-2xl bg-white dark:bg-zinc-950 shadow-xl border border-zinc-200 dark:border-white/10 flex flex-col">

    <!-- Header -->
    <div
      class="flex items-center justify-between border-b border-zinc-200 dark:border-white/10 px-4 py-3 bg-zinc-50 dark:bg-zinc-900">
      <div>
        <h3 class="text-lg font-semibold text-main-dark">
          {{ isAggregate ? 'All Companies' : (companyName | titlecase) }}
          <ng-container *ngIf="documentLabel">— {{ documentLabel }}</ng-container>
        </h3>
        <p class="text-xs text-main-secondary mt-0.5">
          {{ status === 'willExpire' ? 'The following documents are about to expire.' : 'The following documents have expired.' }}
        </p>
      </div>
      <button (click)="onCloseModal()"
        class="rounded-md bg-transparent text-zinc-500 hover:text-zinc-700 dark:text-white dark:hover:text-zinc-300">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto space-y-4 p-4">

      <!-- Loading -->
      <div *ngIf="loading" class="flex items-center justify-center h-40 text-sm text-zinc-500 dark:text-zinc-400">
        Loading...
      </div>

      <!-- Error -->
      <div *ngIf="!loading && error" class="flex items-center justify-center h-40 text-sm text-rose-500">
        Failed to load expired documents.
      </div>

      <ng-container *ngIf="!loading && !error">

        <!-- Expired -->
        <div *ngIf="status === 'expired'" class="space-y-1">
          <h2 class="subheading text-black dark:text-white">Expired ({{ expiredResults.length }})</h2>

          <div *ngFor="let group of expiredGroups" class="space-y-1" [class.mb-3]="isAggregate">
            <h3 *ngIf="isAggregate" class="text-xs font-semibold text-primary uppercase tracking-wide px-0.5">
              {{ group.companyName ? (group.companyName | titlecase) : 'Unknown Company' }}
            </h3>
            <ul role="list"
              class="text-sm-light space-y-1 rounded-lg border p-2 border-rose-200 dark:border-rose-900/40 bg-rose-50/60 dark:bg-rose-950/10">
              <li *ngFor="let item of group.items" class="flex items-start justify-between gap-2 py-1">
                <div class="flex items-center space-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="size-4 text-rose-500 shrink-0" fill="none" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-8.99 4.5h.008v.008h-.008v-.008Z" />
                  </svg>
                  <div>
                    <div class="text-sm-dark font-medium cursor-pointer hover:underline" (click)="onItemClick(item, group.companyId)">{{ item.displayName || item.name | titlecase }}</div>
                    <div class="text-xs text-zinc-500 dark:text-zinc-400">
                      <ng-container *ngIf="entityType === 'vehicle'; else contactInfo">
                        <ng-container *ngIf="item.licensePlateNumber">Plate: {{ item.licensePlateNumber }}</ng-container>
                        <ng-container *ngIf="item.vin"> · VIN: {{ item.vin }}</ng-container>
                      </ng-container>
                      <ng-template #contactInfo>
                        {{ item.email }}<ng-container *ngIf="item.contactNumber || item.phoneNumber"> · {{ item.contactNumber || item.phoneNumber }}</ng-container>
                      </ng-template>
                    </div>
                  </div>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-sm-dark font-medium">{{ item.expireDate | date:'MM/DD/YYYY' }}</p>
                  <p class="text-xs text-rose-500">{{ -item.daysLeft }} days ago</p>
                </div>
              </li>
            </ul>
          </div>

          <div *ngIf="!expiredResults.length" class="text-center text-sm-light text-zinc-500 dark:text-zinc-400 py-2">
            No expired documents.
          </div>
        </div>

        <!-- Will Expire -->
        <div *ngIf="status === 'willExpire'" class="space-y-1">
          <h2 class="subheading text-black dark:text-white">Will Expire ({{ willExpireResults.length }})</h2>

          <div *ngFor="let group of willExpireGroups" class="space-y-1" [class.mb-3]="isAggregate">
            <h3 *ngIf="isAggregate" class="text-xs font-semibold text-primary uppercase tracking-wide px-0.5">
              {{ group.companyName ? (group.companyName | titlecase) : 'Unknown Company' }}
            </h3>
            <ul role="list"
              class="text-sm-light space-y-1 rounded-lg border p-2 border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/10">
              <li *ngFor="let item of group.items" class="flex items-start justify-between gap-2 py-1">
                <div class="flex items-center space-x-1">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="size-4 text-amber-500 shrink-0" fill="none" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-8.99 4.5h.008v.008h-.008v-.008Z" />
                  </svg>
                  <div>
                    <div class="text-sm-dark font-medium cursor-pointer hover:underline" (click)="onItemClick(item, group.companyId)">{{ item.displayName || item.name | titlecase }}</div>
                    <div class="text-xs text-zinc-500 dark:text-zinc-400">
                      <ng-container *ngIf="entityType === 'vehicle'; else contactInfoWillExpire">
                        <ng-container *ngIf="item.licensePlateNumber">Plate: {{ item.licensePlateNumber }}</ng-container>
                        <ng-container *ngIf="item.vin"> · VIN: {{ item.vin }}</ng-container>
                      </ng-container>
                      <ng-template #contactInfoWillExpire>
                        {{ item.email }}<ng-container *ngIf="item.contactNumber || item.phoneNumber"> · {{ item.contactNumber || item.phoneNumber }}</ng-container>
                      </ng-template>
                    </div>
                  </div>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-sm-dark font-medium">{{ item.expireDate | date:'MM/DD/YYYY' }}</p>
                  <p class="text-xs text-amber-600 dark:text-amber-400">{{ item.daysLeft === 0 ? 'Today' : item.daysLeft + ' days left' }}</p>
                </div>
              </li>
            </ul>
          </div>

          <div *ngIf="!willExpireResults.length" class="text-center text-sm-light text-zinc-500 dark:text-zinc-400 py-2">
            No documents expiring soon.
          </div>
        </div>

      </ng-container>
    </div>
  </div>
</div>
